const UNAUTHORIZED_EVENT = "nx:unauthorized";
const SESSION_UPDATED_EVENT = "nx:register-session-updated";
const LOGOUT_EVENT = "nx:register-logout";
const IDP_LOGIN_URL = "https://admin.naturenix.jp/admin/login.html";
const PARTNER_CONSOLE_BLOCKED_DOMAINS = new Set(["number-1.co.jp", "eplusmirai.com"]);

type IdpSession = {
  email?: string;
  role?: string;
  external?: boolean;
  apps?: string[];
};

export type SupportConsoleAccess =
  | { ok: true; session: IdpSession }
  | { ok: false; reason: "unauthenticated" | "forbidden" };

let idpSession: IdpSession | null = null;
let idpAccessToken: string | null = null;
let idpSessionChecked = false;

export function getIdpLoginUrl(nextUrl?: string): string {
  const next = nextUrl || window.location.href;
  return `${IDP_LOGIN_URL}?next=${encodeURIComponent(next)}`;
}

export function redirectToIdpLogin(nextUrl?: string): void {
  window.location.href = getIdpLoginUrl(nextUrl);
}

function emailDomain(email: string | undefined): string {
  if (!email || !email.includes("@")) return "";
  return email.trim().toLowerCase().split("@").pop() || "";
}

/** サポートコンソール入室可否（社外・No.1・support AppAccess なしは拒否）。 */
export function evaluateSupportConsoleAccess(sess: IdpSession | null): SupportConsoleAccess {
  if (!sess?.role) return { ok: false, reason: "unauthenticated" };
  const domain = emailDomain(sess.email);
  if (sess.external === true || PARTNER_CONSOLE_BLOCKED_DOMAINS.has(domain)) {
    return { ok: false, reason: "forbidden" };
  }
  const apps = Array.isArray(sess.apps) ? sess.apps : [];
  if (!apps.includes("support")) {
    return { ok: false, reason: "forbidden" };
  }
  return { ok: true, session: sess };
}

export async function refreshIdpSession(): Promise<IdpSession | null> {
  try {
    const res = await fetch("/api/auth/me", { credentials: "include" });
    if (!res.ok) {
      idpSession = null;
      idpAccessToken = null;
      idpSessionChecked = true;
      window.dispatchEvent(new CustomEvent(SESSION_UPDATED_EVENT));
      return null;
    }
    const data = (await res.json()) as IdpSession & { access_token?: string; apps?: unknown };
    idpAccessToken =
      typeof data.access_token === "string" && data.access_token.trim() ? data.access_token.trim() : null;
    const apps = Array.isArray(data.apps)
      ? data.apps.filter((a): a is string => typeof a === "string")
      : [];
    idpSession = {
      email: typeof data.email === "string" ? data.email : undefined,
      role: typeof data.role === "string" ? data.role : undefined,
      external: data.external === true,
      apps,
    };
    idpSessionChecked = true;
    window.dispatchEvent(new CustomEvent(SESSION_UPDATED_EVENT));
    return idpSession;
  } catch {
    idpSession = null;
    idpAccessToken = null;
    idpSessionChecked = true;
    return null;
  }
}

export function getIdpSession(): IdpSession | null {
  return idpSession;
}

export function hasIdpSession(): boolean {
  return !!idpSession?.role;
}

export function logoutRegisterAdmin(): void {
  idpSession = null;
  idpAccessToken = null;
  try {
    window.localStorage.removeItem("register-admin-session");
    window.localStorage.removeItem("register-admin-remember");
  } catch {
    // ignore
  }
  window.dispatchEvent(new CustomEvent(LOGOUT_EVENT));
  window.dispatchEvent(new CustomEvent(SESSION_UPDATED_EVENT));
}

export async function logoutAllSessions(): Promise<void> {
  try {
    await fetch("/api/auth/logout", { method: "POST", credentials: "include" });
  } catch {
    // ignore
  }
  logoutRegisterAdmin();
}

export function getRegisterAdminToken(): string | null {
  return idpAccessToken;
}

export function getRegisterAdminRole(): string | null {
  return idpSession?.role ?? null;
}

export function getRegisterAdminEmail(): string | null {
  return idpSession?.email ?? null;
}

export function hasRegisterAdminToken(): boolean {
  return hasIdpSession() && !!idpAccessToken;
}

export function hasAuthSession(): boolean {
  return evaluateSupportConsoleAccess(idpSession).ok;
}

export function isAuthBootstrapComplete(): boolean {
  return idpSessionChecked;
}

function shouldAttachAuth(url: URL): boolean {
  if (url.origin !== window.location.origin) return false;
  if (!url.pathname.startsWith("/api/")) return false;
  if (url.pathname.startsWith("/api/tiles/")) return false;
  return true;
}

export function installApiAuthFetch(): void {
  const originalFetch = window.fetch.bind(window);

  window.fetch = async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    const requestUrl =
      input instanceof Request
        ? new URL(input.url, window.location.origin)
        : new URL(String(input), window.location.origin);

    if (!shouldAttachAuth(requestUrl)) {
      return originalFetch(input, init);
    }

    const token = getRegisterAdminToken();
    const headers = new Headers(input instanceof Request ? input.headers : undefined);
    if (init?.headers) {
      new Headers(init.headers).forEach((value, key) => headers.set(key, value));
    }
    if (token && !headers.has("Authorization")) {
      headers.set("Authorization", `Bearer ${token}`);
    }

    const response = await originalFetch(input, {
      ...init,
      headers,
      credentials: init?.credentials ?? "include",
    });
    if (response.status === 401) {
      window.dispatchEvent(new CustomEvent(UNAUTHORIZED_EVENT));
    }
    return response;
  };
}
